<template>
  <div class="form-richtext-wrapper" :style="wrapperStyle">
    <input type="hidden" :name="uniqueTitle" :value="htmlContent" />
    <div v-if="fieldLabel" class="form-field-label">{{ fieldLabel }}</div>
    <div class="rich-text-editor" @dragover.prevent @drop.prevent="onDropFallback">
      <div class="rich-text-toolbar" role="toolbar" :aria-label="fieldLabel ? fieldLabel + ' formatting' : 'Formatting'">
        <div class="toolbar-group">
          <button type="button" class="toolbar-btn" :class="{ active: editor && editor.isActive('bold') }" :aria-pressed="editor && editor.isActive('bold') ? 'true' : 'false'" aria-label="Bold" @click="editor && editor.chain().focus().toggleBold().run()" title="Bold">
            <strong>B</strong>
          </button>
          <button type="button" class="toolbar-btn" :class="{ active: editor && editor.isActive('italic') }" :aria-pressed="editor && editor.isActive('italic') ? 'true' : 'false'" aria-label="Italic" @click="editor && editor.chain().focus().toggleItalic().run()" title="Italic">
            <em>I</em>
          </button>
          <button type="button" class="toolbar-btn" :class="{ active: editor && editor.isActive('underline') }" :aria-pressed="editor && editor.isActive('underline') ? 'true' : 'false'" aria-label="Underline" @click="editor && editor.chain().focus().toggleUnderline().run()" title="Underline">
            <u>U</u>
          </button>
          <button type="button" class="toolbar-btn" :class="{ active: editor && editor.isActive('strike') }" :aria-pressed="editor && editor.isActive('strike') ? 'true' : 'false'" aria-label="Strikethrough" @click="editor && editor.chain().focus().toggleStrike().run()" title="Strikethrough">
            <s>S</s>
          </button>
        </div>
        <div class="toolbar-group">
          <select class="toolbar-select" aria-label="Font" @change="setFontFamily($event.target.value)" :value="currentFontFamily">
            <option value="">Font</option>
            <option value="Inter, sans-serif">Sans Serif</option>
            <option value="Georgia, serif">Serif</option>
            <option value="monospace">Monospace</option>
            <option value="cursive">Cursive</option>
          </select>
          <select class="toolbar-select" aria-label="Font size" @change="setFontSize($event.target.value)" :value="currentFontSize">
            <option value="">Size</option>
            <option value="12px">12</option>
            <option value="14px">14</option>
            <option value="16px">16</option>
            <option value="18px">18</option>
            <option value="20px">20</option>
            <option value="24px">24</option>
            <option value="28px">28</option>
            <option value="32px">32</option>
          </select>
        </div>
        <div class="toolbar-group">
          <button type="button" class="toolbar-btn" :class="{ active: editor && editor.isActive('codeBlock') }" :aria-pressed="editor && editor.isActive('codeBlock') ? 'true' : 'false'" aria-label="Code Block" @click="editor && editor.chain().focus().toggleCodeBlock().run()" title="Code Block">
            &lt;/&gt;
          </button>
        </div>
        <div class="toolbar-group">
          <div class="emoji-picker-wrapper">
            <button type="button" class="toolbar-btn" :aria-expanded="showEmojiPicker ? 'true' : 'false'" aria-label="Emoji" @click="showEmojiPicker = !showEmojiPicker" title="Emoji">
              &#9786;
            </button>
            <div v-if="showEmojiPicker" class="emoji-picker" @mouseleave="showEmojiPicker = false">
              <button type="button" v-for="emoji in emojis" :key="emoji" class="emoji-item" :aria-label="emoji" @click="insertEmoji(emoji)">{{ emoji }}</button>
            </div>
          </div>
        </div>
        <div class="toolbar-group">
          <button type="button" class="toolbar-btn" aria-label="Insert Image" @click="$refs.imageInput.click()" title="Insert Image">
            &#128247;
          </button>
          <button type="button" class="toolbar-btn" aria-label="Attach File" @click="$refs.fileInput.click()" title="Attach File">
            &#128206;
          </button>
        </div>
      </div>
      <editor-content :editor="editor" class="rich-text-content" role="textbox" aria-multiline="true" :aria-label="fieldLabel ? fieldLabel + ', rich text' : 'Rich text'" />
      <input ref="imageInput" type="file" accept="image/*" style="display: none" @change="onImageSelected" />
      <input ref="fileInput" type="file" style="display: none" @change="onFileSelected" />
    </div>
  </div>
</template>

<script>
import { inject, computed } from 'vue'
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import { Underline } from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { FontFamily } from '@tiptap/extension-font-family'
import { Image } from '@tiptap/extension-image'
import { Link } from '@tiptap/extension-link'
import { Placeholder } from '@tiptap/extension-placeholder'
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight'
import { common, createLowlight } from 'lowlight'
import axios from 'axios'
import { formattingToStyle } from '../../lib/client-rule-evaluator'

const lowlight = createLowlight(common)

const FontSize = TextStyle.extend({
  addAttributes () {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: element => element.style.fontSize || null,
        renderHTML: attributes => {
          if (!attributes.fontSize) return {}
          return { style: `font-size: ${attributes.fontSize}` }
        }
      }
    }
  }
})

export default {
  name: 'FormRichText',
  components: { EditorContent },
  props: ['placeholder', 'uniqueTitle', 'fieldLabel', 'description'],
  setup (props) {

    const formFormatting = inject('formFormatting', null)
    const formReadonly = inject('formReadonly', null)
    const updateFormValue = inject('updateFormValue', null)

    const wrapperStyle = computed(() => {
      if (!formFormatting || !props.uniqueTitle) return {}
      const fmt = formFormatting[props.uniqueTitle]
      return fmt ? formattingToStyle(fmt) : {}
    })

    const isReadonly = computed(() => {
      if (!formReadonly || !props.uniqueTitle) return false
      return !!formReadonly[props.uniqueTitle]
    })

    return { formFormatting, formReadonly, updateFormValue, wrapperStyle, isReadonly }
  },
  data () {
    return {
      editor: null,
      htmlContent: '',
      showEmojiPicker: false,
      emojis: [
        '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊',
        '😇', '🙂', '😉', '😍', '🥰', '😘', '😗', '😋',
        '😛', '😜', '🤪', '😎', '🤩', '🥳', '😏', '😒',
        '😞', '😔', '😟', '😕', '😣', '😖', '😫', '😩',
        '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯',
        '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓',
        '👍', '👎', '👏', '🙌', '🤝', '🙏', '💪', '🎉',
        '❤️', '🔥', '⭐', '💯', '✅', '❌', '⚡', '🚀'
      ]
    }
  },
  computed: {
    currentFontFamily () {
      if (!this.editor) return ''
      const attrs = this.editor.getAttributes('textStyle')
      return attrs.fontFamily || ''
    },
    currentFontSize () {
      if (!this.editor) return ''
      const attrs = this.editor.getAttributes('textStyle')
      return attrs.fontSize || ''
    },
    uploadUrl () {
      const application = this.$route && this.$route.params && this.$route.params.application
      const account = this.routeAccount || 'default'
      if (typeof VUE_APP_PROXY_SERVER_URL !== 'undefined' && application) {
        return `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/richtext/assets`
      }
      if (typeof VUE_APP_PROXY_SERVER_URL !== 'undefined') {
        return `${VUE_APP_PROXY_SERVER_URL}/richtext/assets`
      }
      return '/richtext/assets'
    }
  },
  watch: {
    isReadonly (val) {
      if (this.editor) {
        this.editor.setEditable(!val)
      }
    }
  },
  mounted () {
    this.editor = new Editor({
      content: '',
      extensions: [
        StarterKit.configure({ codeBlock: false }),
        Underline,
        FontSize,
        FontFamily,
        Image.configure({
          inline: true,
          allowBase64: false,
          HTMLAttributes: { class: 'rich-text-image' }
        }),
        Link.configure({ openOnClick: false }),
        Placeholder.configure({ placeholder: this.placeholder || 'Start typing...' }),
        CodeBlockLowlight.configure({ lowlight })
      ],
      onUpdate: ({ editor }) => {
        this.htmlContent = editor.getHTML()
        if (this.updateFormValue) {
          this.updateFormValue(this.uniqueTitle, editor.getText())
        }
      },
      editorProps: {
        handleDrop: (view, event, slice, moved) => {
          if (moved) return false
          const files = event.dataTransfer && event.dataTransfer.files
          if (!files || files.length === 0) return false
          event.preventDefault()
          for (let i = 0; i < files.length; i++) {
            const file = files[i]
            if (file.type.startsWith('image/')) {
              this.uploadAndInsertImage(file)
            } else {
              this.uploadAndInsertFile(file)
            }
          }
          return true
        },
        handlePaste: (view, event) => {
          const items = event.clipboardData && event.clipboardData.items
          if (!items) return false
          for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
              event.preventDefault()
              const file = items[i].getAsFile()
              if (file) this.uploadAndInsertImage(file)
              return true
            }
          }
          return false
        }
      }
    })
  },
  beforeUnmount () {
    if (this.editor) this.editor.destroy()
  },
  methods: {
    setFontFamily (font) {
      if (!this.editor) return
      if (font) {
        this.editor.chain().focus().setFontFamily(font).run()
      } else {
        this.editor.chain().focus().unsetFontFamily().run()
      }
    },
    setFontSize (size) {
      if (!this.editor) return
      if (size) {
        this.editor.chain().focus().setMark('textStyle', { fontSize: size }).run()
      } else {
        this.editor.chain().focus().unsetMark('textStyle').run()
      }
    },
    insertEmoji (emoji) {
      if (!this.editor) return
      this.editor.chain().focus().insertContent(emoji).run()
      this.showEmojiPicker = false
    },
    onDropFallback (event) {
      // Only handle drops that didn't land on the ProseMirror editor
      // (Tiptap's handleDrop already handles those)
      if (event.target.closest && event.target.closest('.ProseMirror')) return
      const files = event.dataTransfer && event.dataTransfer.files
      if (!files || files.length === 0) return
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (file.type.startsWith('image/')) {
          this.uploadAndInsertImage(file)
        } else {
          this.uploadAndInsertFile(file)
        }
      }
    },
    onImageSelected (event) {
      const file = event.target.files[0]
      if (file) this.uploadAndInsertImage(file)
      event.target.value = ''
    },
    onFileSelected (event) {
      const file = event.target.files[0]
      if (file) this.uploadAndInsertFile(file)
      event.target.value = ''
    },
    formatFileSize (bytes) {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
    async uploadAndInsertImage (file) {
      const formData = new FormData()
      formData.append('file', file)
      try {
        const response = await axios.post(this.uploadUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const origin = typeof VUE_APP_PROXY_SERVER_URL !== 'undefined' ? VUE_APP_PROXY_SERVER_URL : ''
        const assetUrl = `${origin}${response.data.url}`
        this.editor.chain().focus().setImage({ src: assetUrl, alt: file.name }).run()
      } catch (error) {
        console.error('Failed to upload image:', error)
      }
    },
    async uploadAndInsertFile (file) {
      const formData = new FormData()
      formData.append('file', file)
      try {
        const response = await axios.post(this.uploadUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const origin = typeof VUE_APP_PROXY_SERVER_URL !== 'undefined' ? VUE_APP_PROXY_SERVER_URL : ''
        const assetUrl = `${origin}${response.data.url}`
        const sizeStr = this.formatFileSize(response.data.size)
        const linkHtml = `<a href="${assetUrl}" target="_blank">${response.data.filename} (${sizeStr})</a>&nbsp;`
        this.editor.chain().focus().insertContent(linkHtml).run()
      } catch (error) {
        console.error('Failed to upload file:', error)
      }
    }
  }
}
</script>

<style>
.form-richtext-wrapper {
  width: 100%;
}

.form-richtext-wrapper .rich-text-editor {
  border: 1px solid #ced4da;
  border-radius: 4px;
  background-color: #fff;
  overflow: hidden;
}

.form-richtext-wrapper .rich-text-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 8px;
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
  align-items: center;
}

.form-richtext-wrapper .toolbar-group {
  display: flex;
  gap: 2px;
  align-items: center;
  padding-right: 8px;
  border-right: 1px solid #dee2e6;
  margin-right: 4px;
}

.form-richtext-wrapper .toolbar-group:last-child {
  border-right: none;
  padding-right: 0;
  margin-right: 0;
}

.form-richtext-wrapper .toolbar-btn {
  background: none;
  border: 1px solid transparent;
  color: #495057;
  cursor: pointer;
  padding: 3px 7px;
  border-radius: 3px;
  font-size: 13px;
  line-height: 1.2;
  min-width: 28px;
  text-align: center;
  transition: background-color 0.15s, color 0.15s;
}

.form-richtext-wrapper .toolbar-btn:hover {
  background-color: #e9ecef;
  color: #212529;
}

.form-richtext-wrapper .toolbar-btn.active {
  background-color: #0d6efd;
  color: #fff;
}

.form-richtext-wrapper .toolbar-select {
  background-color: #fff;
  border: 1px solid #ced4da;
  color: #495057;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 12px;
  cursor: pointer;
  max-width: 100px;
}

.form-richtext-wrapper .emoji-picker-wrapper {
  position: relative;
}

.form-richtext-wrapper .emoji-picker {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 2px;
  width: 280px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.form-richtext-wrapper .emoji-item {
  cursor: pointer;
  padding: 4px;
  text-align: center;
  border-radius: 3px;
  font-size: 18px;
  line-height: 1;
  transition: background-color 0.1s;
}

.form-richtext-wrapper .emoji-item:hover {
  background-color: #e9ecef;
}

.form-richtext-wrapper .rich-text-content {
  min-height: 150px;
  max-height: 500px;
  overflow-y: auto;
}

.form-richtext-wrapper .rich-text-content .ProseMirror {
  padding: 12px;
  min-height: 150px;
  color: #212529;
  font-size: 14px;
  line-height: 1.6;
  outline: none;
}

.form-richtext-wrapper .rich-text-content .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}

.form-richtext-wrapper .rich-text-content .ProseMirror p {
  margin: 0 0 0.5em 0;
}

.form-richtext-wrapper .rich-text-content .ProseMirror img.rich-text-image {
  max-width: 200px;
  max-height: 150px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #dee2e6;
  vertical-align: middle;
  margin: 2px 4px;
}

.form-richtext-wrapper .rich-text-content .ProseMirror pre {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 12px;
  margin: 8px 0;
  overflow-x: auto;
}

.form-richtext-wrapper .rich-text-content .ProseMirror pre code {
  color: #212529;
  font-family: monospace;
  font-size: 13px;
  background: none;
  padding: 0;
}

.form-richtext-wrapper .rich-text-content .ProseMirror code {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  padding: 1px 4px;
  font-family: monospace;
  font-size: 13px;
  color: #d63384;
}

.form-richtext-wrapper .rich-text-content .ProseMirror a {
  color: #0d6efd;
  text-decoration: underline;
}
</style>
